import { useMutation, useQuery } from "@apollo/client";
import { CameraIcon, UserIcon, UsersIcon } from "@heroicons/react/24/solid";
import { Button, Skeleton } from "@mui/material";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import { NavIcon } from "../../components/NavMenu/navItems";
import Page from "../../components/Page/Page";
import { CALC_POPULARITY, FIX_PHOTOS } from "../../graphql/mutations-gql";
import { DASHBOARD } from "../../graphql/queries-gql";

type Tone = "primary" | "green" | "gray" | "red" | "orange";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const MetricTile = styled.div<{ $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  background-color: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
  min-width: 0;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  ${(props) =>
    props.$clickable
      ? `
    cursor: pointer;
    &:hover {
      border-color: ${props.theme.borderStrong};
      background-color: ${props.theme.surfaceHover};
    }
    &:focus-visible {
      outline: 2px solid ${props.theme.colors.primary};
      outline-offset: 2px;
    }
  `
      : ""}
`;

const MetricHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricIcon = styled.span<{ $tone?: Tone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex-shrink: 0;
  color: ${(props) =>
    props.$tone ? props.theme.colors[props.$tone] : props.theme.textColor};
  background: ${(props) =>
    props.$tone
      ? props.theme.colors[`${props.$tone}Soft` as const]
      : props.theme.surfaceActive};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MetricLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const MetricValue = styled.span`
  font-size: 26px;
  font-weight: 650;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.textColor};
`;

// StatCard has no click handler, so the dashboard keeps a local tile that
// can link through to the related page.
const Metric: React.FC<{
  label: string;
  value: React.ReactNode;
  Icon: NavIcon;
  tone?: Tone;
  onClick?: () => void;
}> = ({ label, value, Icon, tone, onClick }) => {
  return (
    <MetricTile
      $clickable={onClick !== undefined}
      onClick={onClick}
      role={onClick ? "link" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <MetricHeader>
        <MetricIcon $tone={tone}>
          <Icon />
        </MetricIcon>
        <MetricLabel>{label}</MetricLabel>
      </MetricHeader>
      <MetricValue>{value}</MetricValue>
    </MetricTile>
  );
};

const Dashboard = () => {
  const { data, loading, error } = useQuery(DASHBOARD, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("onCompleted", data);
    },
  });
  const navigate = useNavigate();
  const [calcPopularity] = useMutation(CALC_POPULARITY);
  const [fixPhotos] = useMutation(FIX_PHOTOS);

  const handleCalcPopularity = () => {
    calcPopularity({});
  };

  const actions = (
    <Button
      variant="outlined"
      color="inherit"
      size="small"
      onClick={() => handleCalcPopularity()}
    >
      Calc popularity
    </Button>
  );

  if (loading) {
    return (
      <Page title="Dashboard" actions={actions}>
        <MetricGrid>
          {[...Array(10)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={100} />
          ))}
        </MetricGrid>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Dashboard" actions={actions}>
        <ErrorAlert error={error} />
      </Page>
    );
  }

  return (
    <Page title="Dashboard" actions={actions}>
      <Section>
        <SectionTitle>Activity</SectionTitle>
        <MetricGrid>
          <Metric
            label="DAU"
            value={data.dashboard.dailyActiveUsers}
            Icon={UserIcon}
            tone="primary"
            onClick={() => navigate("/users")}
          />
        </MetricGrid>
      </Section>

      <Section>
        <SectionTitle>Users</SectionTitle>
        <MetricGrid>
          <Metric
            label="Verified users"
            value={data.dashboard.totalUsers}
            Icon={UserIcon}
            tone="green"
            onClick={() => navigate("/users")}
          />
          <Metric
            label="With photo"
            value={data.dashboard.totalUsersWithApprovedPhoto}
            Icon={UserIcon}
            tone="green"
            onClick={() => navigate("/users")}
          />
          <Metric
            label="Deactivated users"
            value={0}
            Icon={UserIcon}
            tone="gray"
          />
          <Metric
            label="Unverified users"
            value={data.dashboard.totalUnverifiedUsers}
            Icon={UserIcon}
            tone="red"
          />
          <Metric
            label="Invited users"
            value={data.dashboard.totalInvites}
            Icon={UsersIcon}
            tone="orange"
          />
        </MetricGrid>
      </Section>

      <Section>
        <SectionTitle>Groups and moderation</SectionTitle>
        <MetricGrid>
          <Metric
            label="Groups"
            value={data.dashboard.totalGroups}
            Icon={UsersIcon}
            tone="green"
          />
          <Metric
            label="Unverified photos"
            value={data.dashboard.totalUnverifiedPhotos}
            Icon={CameraIcon}
            tone="orange"
            onClick={() => navigate("/unverified-photos")}
          />
          {/* <Card width={180} height={80}>
            <FlexCol>
              <Text weight={600}>Unresolved reports</Text>
              <FlexRow justifyContent="center" colGap={10}>
                <UserIcon width={30} />
                <Text weight={600} size={24}>
                  {data.dashboard.totalUnresolvedReports}
                </Text>
              </FlexRow>
            </FlexCol>
          </Card> */}
          <Metric
            label="Banned users"
            value={data.dashboard.totalBannedUsers}
            Icon={UserIcon}
          />
        </MetricGrid>
      </Section>

      <Section>
        <SectionTitle>Content</SectionTitle>
        <MetricGrid>
          <Metric
            label="Private photos"
            value={data.dashboard.totalPrivatePhotos}
            Icon={CameraIcon}
            tone="primary"
          />
        </MetricGrid>
      </Section>
      {/* <FlexRow>
        <StdButton onClick={() => fixPhotos({})}>Fix photos</StdButton>
      </FlexRow> */}
    </Page>
  );
};
export default Dashboard;
